'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PortalLink } from '@akademi/site-builder-runtime';
import { Button, Card } from '@akademi/ui';
import { LevelBadge } from '../_shared/LevelBadge';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { Stars } from '../_shared/Stars';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { isEmbedUrl, PreviewEmbed, PreviewVideo } from '../_shared/CoursePreviewMedia';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { CertificateBadge } from '../course-detail/shared/CertificateBadge';
import { courseCtaLabel } from '../_shared/courseCtaLabel';
import { useQuickStart } from '../_shared/useQuickStart';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type {
  ButtonRadius,
  CertificateDisplay,
  PopoverPosition,
  ProgressTextPlace,
} from './registry';
import { cardReset } from '../_shared/styles';

const UDEMY_CARD_CSS = `
/* Above the rail's nav arrows (ContentRail's ".rail-arrow", z-index
   var(--rail-arrow-z, 20)) so an open hover popover fully covers them
   instead of an arrow circle floating on top of the popover content. */
.lms-udemy-card[data-hovered="true"] { z-index: 30; }
.lms-udemy-card-anchor { display: block; text-decoration: none; color: inherit; }
.lms-udemy-card-anchor:focus-visible {
  outline: 2px solid var(--color-brand-600, #00b4d8);
  outline-offset: 2px;
}
@keyframes lms-udemy-popover-fade {
  from { opacity: 0; transform: translateX(-6px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes lms-udemy-popover-fade-center {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.lms-udemy-card-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.58);
  opacity: 0;
  transition: opacity 160ms ease;
  pointer-events: none;
}
.lms-udemy-card:hover .lms-udemy-card-play,
.lms-udemy-card-anchor:focus-visible .lms-udemy-card-play {
  opacity: 1;
}
.lms-udemy-card-play-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  border: 2px solid rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
}
.lms-udemy-popover-title-link {
  text-decoration: none;
  color: inherit;
}
.lms-udemy-popover-title-link:hover {
  color: var(--color-brand-600, #00b4d8);
}
@media (prefers-reduced-motion: reduce) {
  .lms-udemy-card-play { transition: none; }
}
`;

const PREVIEW_WIDTH = 340;
const PREVIEW_MARGIN = 16;
const POPOVER_MIN_VIEWPORT = 768;

/**
 * Stand-in average used when the course carries no {@code ratingAverage}.
 *
 * <p>The legacy catalog index (`/v1/public/{tenantId}/catalog/search`) does
 * not yet emit `attr.ratingAverage` — course documents only ship
 * `likes`/`unlikes`/`commentCount` — so gating the star row on a real
 * average hid it on every card. Real values win whenever the backend
 * supplies one; this only fills the gap so the row keeps its shape. Delete
 * this constant (and {@link ratingFallback}) once the catalog indexes the
 * average for every tenant.
 */
const RATING_FALLBACK = 0;

function UsersIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ClockIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function EyeIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CommentIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
    </svg>
  );
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace('.0', '')}k`;
  return n.toLocaleString();
}

export function UdemyCourseCard({
  course: c,
  showRating = true,
  buttonRadius,
  certificateDisplay = 'both',
  autoPlayPreview = false,
  popoverPosition = 'right',
  popoverTitleLink = true,
  ratingFallback = RATING_FALLBACK,
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  showRating?: boolean;
  buttonRadius?: ButtonRadius;
  certificateDisplay?: CertificateDisplay;
  /** When a preview clip exists, play it (muted, looped) inside the hover
   *  popover instead of showing the static poster with a play badge.
   *  Admin-managed; only meaningful for courses that carry a
   *  {@code previewVideoUrl}. */
  autoPlayPreview?: boolean;
  /** Where the hover popover opens. {@code 'left'}/{@code 'right'}
   *  (default) pin a side, falling back to the other when there's no room;
   *  {@code 'center'} overlays the popover on the card itself instead of
   *  beside it. Admin-managed. */
  popoverPosition?: PopoverPosition;
  /** When true (default), the popover's title links to the course, same
   *  as the card face. Admin-managed. */
  popoverTitleLink?: boolean;
  /** Average shown when the course has no {@code ratingAverage} of its own
   *  (see {@link RATING_FALLBACK}). Pass {@code null} to go back to hiding
   *  the star row entirely on unrated courses. */
  ratingFallback?: number | null;
  /** Progress bar shown above the footer for courses carrying
   *  {@code course.progress}. {@code show} (default true) toggles the bar
   *  itself; {@code showText} (default false) adds the percentage label;
   *  {@code textPlace} (default {@code 'outside-right'}) positions that
   *  label beside or on top of the track. Admin-managed. */
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const { t } = useTranslation('common');
  const quickStart = useQuickStart(c, t);
  // Attendee status decides the label first; Quick Start only overrides
  // the "not started yet" case with "Start Now" — "Continue Learning"
  // (already in progress) stays as-is since Quick Start would otherwise
  // read as "restart". Approval-required courses always keep "Request"
  // and route through the detail page, same as when Quick Start is off.
  const baseCtaLabel = courseCtaLabel(t, c.attendeeStatus, c.approvalRequired);
  const ctaLabel =
    quickStart.available && !c.approvalRequired && baseCtaLabel === t('label.startLearning')
      ? t('label.startNow')
      : baseCtaLabel;
  const cardRef = useRef<HTMLDivElement>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [hovered, setHovered] = useState(false);
  // Viewport-space coordinates for the popover, computed once on hover-enter.
  // The popover is `position: fixed` (see below) precisely so it is never
  // clipped by an ancestor's `overflow` — a horizontally-scrolling rail
  // needs `overflow-x` set to something other than `visible`, and a
  // position:fixed element is the one thing that's guaranteed to escape
  // that clipping in every browser, no CSS overflow workaround required.
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const onEnter = () => {
    clearTimeout(leaveTimer.current);
    const el = cardRef.current;
    if (!el) return;
    if (window.innerWidth < POPOVER_MIN_VIEWPORT) return;
    const rect = el.getBoundingClientRect();
    if (popoverPosition === 'center') {
      // Centering a popover wider than the card itself (common — PREVIEW_WIDTH
      // vs. a narrower grid/rail card) can push it past the viewport edge for
      // cards near either side, pinning it flush against the edge with zero
      // breathing room. Clamp so it always keeps at least PREVIEW_MARGIN of
      // space from both edges, same margin the 'left'/'right' variants use.
      const rawLeft = rect.left + rect.width / 2 - PREVIEW_WIDTH / 2;
      const clampedLeft = Math.min(
        Math.max(rawLeft, PREVIEW_MARGIN),
        window.innerWidth - PREVIEW_WIDTH - PREVIEW_MARGIN,
      );
      setCoords({ top: rect.top - 12, left: clampedLeft });
      setHovered(true);
      return;
    }
    const roomRight = window.innerWidth - rect.right;
    const roomLeft = rect.left;
    const resolvedSide =
      popoverPosition === 'left'
        ? roomLeft >= PREVIEW_WIDTH + PREVIEW_MARGIN
          ? 'left'
          : roomRight >= roomLeft
            ? 'right'
            : 'left'
        : roomRight >= PREVIEW_WIDTH + PREVIEW_MARGIN
          ? 'right'
          : roomLeft >= roomRight
            ? 'left'
            : 'right';
    setCoords({
      top: rect.top - 12,
      left: resolvedSide === 'right' ? rect.right + 8 : rect.left - PREVIEW_WIDTH - 8,
    });
    setHovered(true);
  };

  const onLeave = () => {
    leaveTimer.current = setTimeout(() => setHovered(false), 100);
  };

  // Fixed positioning is viewport-relative, so it won't track the card if
  // the page (or an ancestor rail) scrolls while hovered — close instead of
  // drifting. `capture: true` catches scroll events fired on the rail's own
  // scroll container too, since 'scroll' doesn't bubble.
  useEffect(() => {
    if (!hovered) return;
    const close = () => setHovered(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [hovered]);

  // Real average when the backend has one, otherwise the fallback — `null`
  // means "no stars at all", which is what the card did before the catalog
  // gap showed up.
  const rating = typeof c.ratingAverage === 'number' ? c.ratingAverage : ratingFallback;

  // Used only for the video poster below — the cover itself renders via
  // <CourseCoverImage>, which resizes/fallbacks internally.
  const imageUrl = typeof c.image === 'string' ? resizeImageUrl(c.image, '680x0') : undefined;
  const hasVideo = Boolean(c.previewVideoUrl);
  // Play the clip inline only when an admin turned autoplay on AND the
  // course actually ships a preview URL. Otherwise the popover keeps the
  // static poster + play badge (or, with no video, the plain cover image).
  const playVideo = hasVideo && autoPlayPreview;
  const hasStats = (c.enrolled ?? 0) > 0 || (c.viewCount ?? 0) > 0 || (c.commentCount ?? 0) > 0;

  const href = `${c.href}`;

  return (
    <div
      ref={cardRef}
      className="lms-udemy-card"
      data-hovered={hovered ? 'true' : undefined}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      // Fills the rail/grid item's cross-stretched box (see ContentRail's
      // `.item` / CSS Grid's default row-stretch) so every card in a row
      // renders the same height regardless of content that shifts its own
      // natural height (e.g. no level badge → 14px less top padding).
      // Without this, `height: '100%'` on the inner PortalLink below
      // resolves against this div's `auto` height and does nothing.
      style={{ position: 'relative', height: '100%' }}
    >
      <style dangerouslySetInnerHTML={{ __html: UDEMY_CARD_CSS }} />

      {/* ── Card face — minimal; whole face is one link ── */}
      <Card asChild>
        <PortalLink
          href={href}
          className="lms-udemy-card-anchor"
          style={{
            ...cardReset,
            background: 'var(--color-surface-card, #fff)',
            border: '1px solid var(--color-border-default, #e0e0e0)',
            borderRadius: 10,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Cover — level badge straddles the bottom edge (half over the
            image, half over the body below) */}
          <div
            style={{
              flexShrink: 0,
              aspectRatio: 'var(--app-course-image-aspect-ratio, 16/9)',
              position: 'relative',
            }}
          >
            <CourseCoverImage
              image={typeof c.image === 'string' ? c.image : undefined}
              gradient="linear-gradient(135deg, var(--color-brand-50, #26c6da) 0%, var(--color-brand-100, #006d82) 100%)"
            />
            <LevelBadge
              level={c.level}
              variant="solid"
              style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                transform: 'translateY(50%)',
                zIndex: 2,
              }}
            />
          </div>

          {/* Body — rating + title. The trainer deliberately lives only in the
            hover popover, keeping the card face minimal. Extra top padding when
            the level badge is present so its overhanging half clears the stars. */}
          <div
            style={{
              // The level badge always renders and straddles the cover's bottom
              // edge, so the body always reserves room for it.
              padding: '26px 16px 14px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {/* Rating — real course average (attr.ratingAverage) when the
              backend supplies one, otherwise `ratingFallback`. */}
            {showRating && typeof rating === 'number' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Stars value={rating} />
                <span
                  style={{
                    fontSize: 13,
                    color: 'var(--color-text-secondary, #4a4a4a)',
                    fontWeight: 700,
                  }}
                >
                  {rating.toFixed(1)}
                </span>
                {c.ratingCount ? (
                  <span style={{ fontSize: 13, color: 'var(--color-text-tertiary, #6a6f73)' }}>
                    ({formatCount(c.ratingCount)} {t('label.comment')})
                  </span>
                ) : null}
              </div>
            )}

            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg, 18px)',
                fontWeight: 700,
                lineHeight: 1.3,
                color: 'var(--color-text-primary, #1c1d1f)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                minHeight: 47,
              }}
            >
              {c.title}
            </h3>
          </div>
          {/* Progress bar — sits above the footer; only rendered when the
            course carries progress data. `textPlace` picks where the
            percentage label renders: beside the track ('outside-*') or
            overlaid on it ('inside-*', centered or pinned to an edge). */}
          {showProgress && typeof c.progress === 'number' && (
            <div style={{ flexShrink: 0, padding: '0 16px 12px' }}>
              <CourseProgressBar
                progress={c.progress}
                showText={showProgressText}
                textPlace={progressTextPlace}
              />
            </div>
          )}
          {/* Footer — enrolled + duration on a subtle bar. Mutually
            exclusive with the progress bar: when "Show progress bar" is
            on, the progress bar owns the bottom-of-card slot instead.
            Always renders otherwise — missing values fall back to a
            placeholder rather than collapsing the bar, so cards in the
            same row stay the same height. */}
          {!showProgress && (
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '12px 16px',
                borderTop: '1px solid var(--color-border-default, #e0e0e0)',
                background: 'var(--color-surface-subtle, #F3F1F2)',
                fontSize: 13,
                color: 'var(--color-text-secondary, #4a4a4a)',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <UsersIcon />
                {c.enrolled && c.enrolled > 0 ? formatCount(c.enrolled) : '0'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ClockIcon />
                {c.duration ?? (c.totalHours ? `${c.totalHours} ${t('label.hours')}` : '-')}
              </span>
            </div>
          )}
        </PortalLink>
      </Card>

      {/* ── Hover popover — all details ──
          `position: fixed` at viewport coordinates computed in `onEnter`
          (see `coords`) — NOT relative to the card — so it paints on top
          of the page and is never clipped by an ancestor's `overflow`
          (e.g. the course rail's horizontal scroll container). 'center'
          overlays the popover on the card; side placements sit beside it. */}
      {hovered && coords && (
        <div
          aria-hidden
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          style={
            popoverPosition === 'center'
              ? {
                  position: 'fixed',
                  top: coords.top,
                  left: coords.left,
                  width: PREVIEW_WIDTH,
                  background: 'var(--color-surface-card, #fff)',
                  border: '1px solid var(--color-border-default, #e0e0e0)',
                  borderRadius: 10,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
                  zIndex: 9999,
                  animation: 'lms-udemy-popover-fade-center 160ms ease-out',
                  overflow: 'hidden',
                }
              : {
                  position: 'fixed',
                  top: coords.top,
                  left: coords.left,
                  width: PREVIEW_WIDTH,
                  background: 'var(--color-surface-card, #fff)',
                  border: '1px solid var(--color-border-default, #e0e0e0)',
                  borderRadius: 10,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
                  zIndex: 9999,
                  animation: 'lms-udemy-popover-fade 160ms ease-out',
                  overflow: 'hidden',
                }
          }
        >
          {/* Preview media — edge to edge. When autoplay is on and a clip
              exists, the muted looping <video> replaces the static poster;
              otherwise it stays a cover image with a play badge. Wrapped in
              the same title link when `popoverTitleLink` is on, so the image
              is clickable too, not just the title text — PreviewVideo's own
              onClick already assumes it's mounted inside a PortalLink (see
              its stopPropagation comment) so this also fixes that contract
              for this card. */}
          {(() => {
            const previewMedia = (
              <div
                style={{
                  aspectRatio: '16/9',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CourseCoverImage
                  image={typeof c.image === 'string' ? c.image : undefined}
                  gradient="linear-gradient(135deg, var(--color-brand-100, #26c6da) 0%, var(--color-brand-50, #006d82) 100%)"
                  hideBrandMark={hasVideo}
                />
                {playVideo &&
                  (isEmbedUrl(c.previewVideoUrl!) ? (
                    <PreviewEmbed src={c.previewVideoUrl!} title={c.title} poster={imageUrl} />
                  ) : (
                    <PreviewVideo src={c.previewVideoUrl!} poster={imageUrl} />
                  ))}
                {hasVideo && !playVideo && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
                )}
                {hasVideo && !playVideo && (
                  <div
                    style={{
                      position: 'relative',
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.15)',
                      border: '2px solid rgba(255,255,255,0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: 22,
                    }}
                  >
                    ▶
                  </div>
                )}
                {/* Play affordance — revealed on card hover/focus, but only when
                    there's a clip to preview. Hidden while it's autoplaying (the
                    moving video is its own affordance) and when there's no video
                    at all (nothing to play → no overlay/button). */}
                {hasVideo && !playVideo && (
                  <div className="lms-udemy-card-play" aria-hidden>
                    <span className="lms-udemy-card-play-btn">▶</span>
                  </div>
                )}
                {hasVideo && !playVideo && (
                  <span
                    style={{
                      position: 'absolute',
                      bottom: 10,
                      right: 10,
                      padding: '3px 10px',
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: 4,
                      letterSpacing: 'var(--font-tracking-wider, 0.06em)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('label.preview')}
                  </span>
                )}
                {/* Level badge — straddles the preview's bottom edge (matches card) */}
                <LevelBadge
                  level={c.level}
                  variant="solid"
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    transform: 'translateY(50%)',
                    zIndex: 2,
                  }}
                />
              </div>
            );
            return popoverTitleLink ? (
              <PortalLink href={href} aria-label={c.title} style={{ display: 'block' }}>
                {previewMedia}
              </PortalLink>
            ) : (
              previewMedia
            );
          })()}
          <div
            style={{
              padding: '26px 16px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {/* Rating — below the title (matches card) */}
            {showRating && typeof rating === 'number' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Stars value={rating} size={14} />
                <span
                  style={{
                    fontSize: 13,
                    color: 'var(--color-text-secondary, #4a4a4a)',
                    fontWeight: 700,
                  }}
                >
                  {rating.toFixed(1)}
                </span>
                <span style={{ fontSize: 13, color: 'var(--color-text-tertiary, #6a6f73)' }}>
                  {(c.commentCount ?? 0) > 0 && <>({formatCount(c.commentCount!)})</>}
                </span>
              </div>
            )}
            {/* Title */}
            <h4
              style={{
                margin: 0,
                fontSize: 'var(--font-size-md, 15px)',
                fontWeight: 700,
                lineHeight: 1.3,
                color: 'var(--color-text-primary, #1c1d1f)',
              }}
            >
              {popoverTitleLink ? (
                <PortalLink href={href} className="lms-udemy-popover-title-link">
                  {c.title}
                </PortalLink>
              ) : (
                c.title
              )}
            </h4>
            {c.description && (
              <span
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  color: 'var(--color-text-secondary, #4a4a4a)',
                  lineHeight: 1.4,
                }}
              >
                {c.description}
              </span>
            )}
            {/* Certificate badge — below the rating; matches course detail.
                Display style (badge / text / both) is admin-configurable. */}
            {c.hasCertificate && (
              <CertificateBadge
                label={t('label.certified')}
                badgeUrl={c.certificateBadgeUrl}
                mode={certificateDisplay}
              />
            )}
            {/* Instructor with avatar + rating */}
            {c.instructor && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {c.instructorAvatar && (
                  <img
                    src={c.instructorAvatar}
                    alt={c.instructor}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--color-text-primary, #1c1d1f)',
                    }}
                  >
                    {c.instructor}
                  </div>
                  {/* {showRating && typeof c.ratingAverage === 'number' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                      <Stars value={c.ratingAverage} />
                      <span
                        style={{
                          fontSize: 11,
                          color: 'var(--color-amber-700, #b45309)',
                          fontWeight: 700,
                        }}
                      >
                        {c.ratingAverage.toFixed(1)}
                      </span>
                      {c.ratingCount && (
                        <span
                          style={{ fontSize: 11, color: 'var(--color-text-tertiary, #6a6f73)' }}
                        >
                          ({formatCount(c.ratingCount)})
                        </span>
                      )}
                    </div>
                  )} */}
                </div>
              </div>
            )}
            {/* Duration + participant count — same row, both bold. */}
            {(c.duration || c.totalHours || (c.enrolled ?? 0) > 0) && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--color-text-tertiary, #6a6f73)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                {(c.duration || c.totalHours) && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <ClockIcon size={16} />
                    {c.duration ?? `${c.totalHours} ${t('label.hours')}`}
                  </span>
                )}
                {(c.enrolled ?? 0) > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <UsersIcon size={16} />
                    <strong>{formatCount(c.enrolled!)}</strong> {t('label.participant')}
                  </span>
                )}
              </div>
            )}
            {/* Stats box */}
            {hasStats && (
              <div
                style={{
                  display: 'flex',
                  // background: 'var(--color-surface-subtle, #f5f5f5)',
                  // border: '1px solid var(--color-border-default, #e0e0e0)',
                  // borderRadius: 6,
                  overflow: 'hidden',
                  fontSize: 12,
                }}
              >
                {(c.viewCount ?? 0) > 0 && (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '7px 8px',
                      color: 'var(--color-text-secondary, #3d3d3d)',
                      borderRight: c.commentCount
                        ? '1px solid var(--color-border-default, #e0e0e0)'
                        : undefined,
                    }}
                  >
                    <EyeIcon size={16} />
                    <span>
                      <strong>{formatCount(c.viewCount!)}</strong> {t('label.display')}
                    </span>
                  </div>
                )}
                {(c.commentCount ?? 0) > 0 && (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '7px 8px',
                      color: 'var(--color-text-secondary, #3d3d3d)',
                    }}
                  >
                    <CommentIcon size={16} />
                    <span>
                      <strong>{c.commentCount}</strong> {t('label.comment')}
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* Outcomes */}
            {c.outcomes && c.outcomes.length > 0 && (
              <ul
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--color-text-secondary, #2d2f31)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                }}
              >
                {c.outcomes.slice(0, 4).map((o, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span
                      style={{
                        color: 'var(--color-brand-600, #00b4d8)',
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      ✓
                    </span>
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            )}
            {/* CTA — when Quick Start is available it always reads "Start Now"
                (the button registers-then-plays, so "continue"/"retake"
                phrasing no longer applies); otherwise the label follows
                attendeeStatus (start / continue / retake), or "Request" when
                the course needs approval. Approval-required courses skip
                Quick Start and just link to the detail page, where the full
                request flow (demand form, etc.) lives. */}
            <Button
              asChild
              radius={buttonRadius}
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={quickStart.loading}
            >
              <PortalLink
                href={c.href}
                onClick={!c.approvalRequired ? quickStart.start : undefined}
              >
                {ctaLabel}
              </PortalLink>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

UdemyCourseCard.gridSpec = {
  kind: 'grid',
  minCardWidth: 180,
  gap: 'clamp(12px, 2vw, 16px)',
} satisfies CardGridSpec;
